import React, { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { Button } from "@material-ui/core";
import MessageDialog, { useMessageDialog } from "../Common/MessageDialog";
import { getGroupRenewalInfo, submitGroupRenewal, getPlanBenefits } from "../../api/groupApi";

// ─── Constants ────────────────────────────────────────────────────────────────

const AFFILIATE_SPONSORS = [
    "Blue Cross Blue Shield of Minnesota",
    "Wellmark Blue Cross Blue Shield of South Dakota",
    "Wellmark Blue Cross Blue Shield of Iowa",
    "Blue Cross Blue Shield of North Dakota",
    "Blue Cross Blue Shield of Nebraska",
];

// const PBS_OPTIONS = ["$10/$25/$40/25%", "Custom"];

const ENROLLMENT_METHODS = ["Paper Enrollment", "Electronic File"];

const PAYMENT_OPTIONS = ["Split Bill", "Bill to Member", "Bill to Group"];

// ─── Component ────────────────────────────────────────────────────────────────

const GroupRenewal = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const groupNumber = searchParams.get("groupNumber") ?? "";
    const [loading, setLoading] = useState(true);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isConfirmed, setIsConfirmed] = useState(false);
    const [planBenefits, setPlanBenefits] = useState([]);
    const { showMessage, dialogProps: errorDialogProps } = useMessageDialog();

    const {
        register,
        handleSubmit,
        watch,
        reset,
        formState: { errors },
    } = useForm({
        mode: "onBlur",
        reValidateMode: "onBlur",
        defaultValues: {
            groupNumber,
            groupName: "",
            currentPlanYear: "",
            renewalPlanYear: "",
            affiliatedPlanSponsor: "",
            requestedEffectiveDate: "",
            planBenefitId: "",
            oeEnabled: false,
            openEnrollmentStartDate: "",
            enrollmentRequestMethod: "",
            paymentOption: "",
            employerGroupPremiumAmount: "",
            lateEnrollmentPayIndicator: false,
            totalPremiumAmount: "",
        },
    });

    const watchPaymentOption = watch("paymentOption");
    const watchOeEnabled = watch("oeEnabled");

    useEffect(() => {
        if (!groupNumber) { setLoading(false); return; }
        const toDateInput = (iso) => iso ? iso.substring(0, 10) : "";
        getGroupRenewalInfo(groupNumber)
            .then((data) => {
                if (data) {
                    reset({
                        groupNumber: data.groupNumber,
                        groupName: data.groupName ?? "",
                        currentPlanYear: data.currentPlanYear?.toString() ?? "",
                        renewalPlanYear: data.renewalPlanYear?.toString() ?? "",
                        affiliatedPlanSponsor: data.affiliatedPlanSponsor ?? "",
                        requestedEffectiveDate: toDateInput(data.requestedEffectiveDate),
                        planBenefitId: data.planBenefitId ?? "",
                        oeEnabled: !!data.openEnrollmentStartDate,
                        openEnrollmentStartDate: toDateInput(data.openEnrollmentStartDate),
                        enrollmentRequestMethod: data.enrollmentRequestMethod ?? "",
                        paymentOption: data.paymentOption ?? "",
                        employerGroupPremiumAmount: data.employerGroupPremiumAmount ?? "",
                        lateEnrollmentPayIndicator: data.lateEnrollmentPayIndicator ?? false,
                        totalPremiumAmount: data.totalPremiumAmount ?? "",
                    });
                    return getPlanBenefits(data.renewalPlanYear);
                }
            })
            .then((benefits) => {
                setPlanBenefits(Array.isArray(benefits) ? benefits : []);
            })
            .catch((err) => console.error("Failed to load renewal info:", err))
            .finally(() => setLoading(false));
    }, [groupNumber, reset]);

    // ─── Submit ────────────────────────────────────────────────────────────────

    const onSubmit = async (data) => {
        setIsProcessing(true);
        try {
            // const payload = {
            //     groupNumber:              data.groupNumber,
            //     groupName:                data.groupName,
            //     currentPlanYear:          data.currentPlanYear ? parseInt(data.currentPlanYear, 10) : null,
            //     renewalPlanYear:          data.renewalPlanYear ? parseInt(data.renewalPlanYear, 10) : null,
            //     affiliatedPlanSponsor:    data.affiliatedPlanSponsor,
            //     requestedEffectiveDate:   data.requestedEffectiveDate || null,
            //     planBenefitId:            data.planBenefitId || null,
            //     enrollmentRequestMethod:  data.enrollmentRequestMethod,
            //     openEnrollmentStartDate:  data.oeEnabled ? (data.openEnrollmentStartDate || null) : null,
            //     lateEnrollmentPayIndicator: data.lateEnrollmentPayIndicator,
            //     paymentOption:            data.paymentOption,
            //     totalPremiumAmount:       data.totalPremiumAmount !== "" ? Number(data.totalPremiumAmount) : null,
            // };
            const payload = { ...data, openEnrollmentStartDate: data.oeEnabled ? (data.openEnrollmentStartDate || null) : null }
            await submitGroupRenewal(payload);
            setIsConfirmed(true);
        } catch (err) {
            await showMessage({
                message: `An error occurred while submitting your request: ${err.message}`,
                title: "YourMedicareSolutions.com",
                messageType: "danger",
            });
        }
        setIsProcessing(false);
    };

    if (loading) {
        return <div role="main" className="main-container pt-md-5 pb-5 container js-quickedit-main-content"><p>Loading...</p></div>;
    }

    // ─── Confirmation screen ───────────────────────────────────────────────────

    if (isConfirmed) {
        return (
            <div role="main" className="main-container pt-md-5 pb-5 container js-quickedit-main-content">
                <div className="row d-md-flex">
                    <section className="col-sm-12 internal-page">
                        <div className="region region-content">

                            <section className="block block-clearstone-updates block-clearstone-breadcrumbs-block clearfix">
                                <div className="breadcrumb-wrap">
                                    <div className="region--breadcrumb nst-1">
                                        <section className="block-system block-system-breadcrumb-block">
                                            <div role="navigation">
                                                <ol className="breadcrumb">
                                                    <li><Link to="/">Home</Link></li>
                                                    <li><Link to="/GroupPlanManagement">Group Plan Management</Link></li>
                                                    <li><Link to={`/GroupDashboard?groupNumber=${groupNumber}`}>Group Dashboard</Link></li>
                                                    <li>Group Renewal</li>
                                                </ol>
                                            </div>
                                        </section>
                                    </div>
                                </div>
                            </section>

                            <h1 className="page-header">Group Renewal</h1>
                            <div style={{ padding: "24px", border: "1px solid #d4edda", borderRadius: "6px", background: "#f0f9f0", textAlign: "center" }}>
                                <div style={{ fontSize: "56px", color: "#16a34a", lineHeight: 1 }}>✓</div>
                                <h2 style={{ color: "#0056a3" }}>Submission Received</h2>
                                <p>Thank you. Your group renewal request has been submitted successfully.</p>
                                <div style={{ display: "flex", justifyContent: "center", gap: "16px", marginTop: "16px", flexWrap: "wrap" }}>
                                    <Button
                                        className="webform-button--next button js-form-submit form-submit btn-default btn"
                                        onClick={() => window.location.href = `/GroupPlanContactInfo?groupNumber=${groupNumber}`}
                                    >
                                        Manage Group Contacts
                                    </Button>
                                    <Button
                                        className="webform-button--next butt-on js-form-submit form-submit btn-default btn"
                                        onClick={() => window.location.href = "/GroupLogin"}
                                    >
                                        Group Plan Management
                                    </Button>
                                </div>
                            </div>

                        </div>
                    </section>
                </div>
            </div>
        );
    }

    // ─── Form ──────────────────────────────────────────────────────────────────

    return (
        <div role="main" className="main-container pt-md-5 pb-5 container js-quickedit-main-content">
            <div className="row d-md-flex">
                <section className="col-sm-12 internal-page">
                    <div className="region region-content">

                        {/* Breadcrumb */}
                        <section className="block block-clearstone-updates block-clearstone-breadcrumbs-block clearfix">
                            <div className="breadcrumb-wrap">
                                <div className="region--breadcrumb nst-1">
                                    <section className="block-system block-system-breadcrumb-block">
                                        <div role="navigation">
                                            <ol className="breadcrumb">
                                                <li><Link to="/">Home</Link></li>
                                                <li><Link to="/GroupPlanManagement">Group Plan Management</Link></li>
                                                <li><Link to={`/GroupDashboard?groupNumber=${groupNumber}`}>Group Dashboard</Link></li>
                                                <li>Group Renewal</li>
                                            </ol>
                                        </div>
                                    </section>
                                </div>
                            </div>
                        </section>

                        <h1 className="page-header">Group Renewal</h1>

                        <form onSubmit={handleSubmit(onSubmit)} noValidate>

                            {/* ══ GROUP INFORMATION ══════════════════════════════════════════ */}
                            <div style={{ padding: "20px", border: "1px solid #e0e0e0", borderRadius: "6px", backgroundColor: "#fafbfd", marginTop: "32px" }}>
                                <h2 style={{ color: "#0056a3", marginTop: 0, marginBottom: "16px" }}>Group Information</h2>

                                {/* Group Number · Group Name */}
                                <div className="row form-group">
                                    <div className="col-sm-6 col-md-6 form-item form-group">
                                        <label htmlFor="groupNumber" className="control-label">Group Number</label>
                                        <input id="groupNumber" type="text" className="form-text form-control"
                                            readOnly {...register("groupNumber")} />
                                    </div>
                                    <div className="col-sm-6 col-md-6 form-item form-group">
                                        <label htmlFor="groupName" className="control-label">Group Name</label>
                                        <input id="groupName" type="text" className="form-text form-control"
                                            readOnly {...register("groupName")} />
                                    </div>
                                </div>

                                {/* Current Plan Year · Renewal Plan Year */}
                                <div className="row form-group">
                                    <div className="col-sm-6 col-md-6 form-item form-group">
                                        <label htmlFor="currentPlanYear" className="control-label">Current Plan Year</label>
                                        <input id="currentPlanYear" type="text" className="form-text form-control"
                                            readOnly {...register("currentPlanYear")} />
                                    </div>
                                    <div className="col-sm-6 col-md-6 form-item form-group">
                                        <label htmlFor="renewalPlanYear" className="control-label">Renewal Plan Year</label>
                                        <input id="renewalPlanYear" type="text" className="form-text form-control"
                                            readOnly {...register("renewalPlanYear")} />
                                    </div>
                                </div>

                                {/* Affiliated Sponsor · Requested Effective Date */}
                                <div className="row form-group">
                                    <div className="col-sm-6 col-md-6 form-item form-group">
                                        <label htmlFor="affiliatedPlanSponsor" className="control-label">
                                            Affiliated Health Plan Sponsor
                                        </label>
                                        <input
                                            id="affiliatedPlanSponsor"
                                            type="text"
                                            className="form-text form-control"
                                            readOnly
                                            {...register("affiliatedPlanSponsor")}
                                        />
                                    </div>
                                    <div className="col-sm-6 col-md-6 form-item form-group">
                                        <label htmlFor="requestedEffectiveDate" className="control-label">
                                            Requested Effective Date
                                        </label>
                                        <input
                                            id="requestedEffectiveDate"
                                            type="text"
                                            className="form-text form-control"
                                            readOnly
                                            {...register("requestedEffectiveDate")}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* ══ PLAN INFORMATION ═══════════════════════════════════════════ */}
                            <div style={{ padding: "20px", border: "1px solid #e0e0e0", borderRadius: "6px", backgroundColor: "#fafbfd", marginTop: "32px" }}>
                                <h2 style={{ color: "#0056a3", marginTop: 0, marginBottom: "16px" }}>Plan Information</h2>

                                {/* PBS · Enrollment Request Method */}
                                <div className="row form-group">
                                    <div className="col-sm-6 col-md-6 form-item form-group">
                                        <label htmlFor="planBenefitId" className="control-label js-form-required form-required">
                                            Plan Benefit Structure
                                        </label>
                                        <select
                                            id="planBenefitId"
                                            className="form-select form-control"
                                            {...register("planBenefitId", { required: "Plan Benefit Structure is required." })}
                                        >
                                            <option value="">Select</option>
                                            {Array.isArray(planBenefits) && planBenefits.map((b) => <option key={b.value} value={b.value}>{b.text}</option>)}
                                        </select>
                                        <ErrorMessage errors={errors} name="planBenefitId"
                                            render={({ message }) => <div role="alert" aria-live="polite" className="error">{message}</div>} />
                                    </div>
                                    {/* <div className="col-sm-6 col-md-6 form-item form-group">
                                        <label htmlFor="enrollmentRequestMethod" className="control-label js-form-required form-required">
                                            Enrollment Request Method
                                        </label>
                                        <select
                                            id="enrollmentRequestMethod"
                                            className="form-select form-control"
                                            {...register("enrollmentRequestMethod", { required: "Enrollment Request Method is required." })}
                                        >
                                            <option value="">Select</option>
                                            {ENROLLMENT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
                                        </select>
                                        <ErrorMessage errors={errors} name="enrollmentRequestMethod"
                                            render={({ message }) => <div role="alert" aria-live="polite" className="error">{message}</div>} />
                                    </div> */}
                                </div>

                                {/* Open Enrollment Start Date (conditional) */}
                                <div className="row form-group">
                                    <div className="col-sm-6 col-md-6 form-item form-group">
                                        <div className="checkbox">
                                            <label>
                                                <input type="checkbox" {...register("oeEnabled")} />
                                                {" "}Open Enrollment Start Date
                                            </label>
                                        </div>
                                        {watchOeEnabled && (
                                            <>
                                                <input
                                                    type="date"
                                                    className="form-text form-control"
                                                    style={{ marginTop: "6px" }}
                                                    {...register("openEnrollmentStartDate", {
                                                        validate: (val) =>
                                                            !watchOeEnabled || !!val || "Open Enrollment Start Date is required.",
                                                    })}
                                                />
                                                <ErrorMessage errors={errors} name="openEnrollmentStartDate"
                                                    render={({ message }) => <div role="alert" aria-live="polite" className="error">{message}</div>} />
                                            </>
                                        )}
                                    </div>

                                </div>

                                {/* Payment Option · Employer Group Premium Amount (conditional) */}
                                <div className="row form-group">
                                    <div className="col-sm-6 col-md-6 form-item form-group">
                                        <label htmlFor="paymentOption" className="control-label js-form-required form-required">
                                            Payment Option
                                        </label>
                                        <select
                                            id="paymentOption"
                                            className="form-select form-control"
                                            {...register("paymentOption", { required: "Payment Option is required." })}
                                        >
                                            <option value="">Select</option>
                                            {PAYMENT_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                                        </select>
                                        <ErrorMessage errors={errors} name="paymentOption"
                                            render={({ message }) => <div role="alert" aria-live="polite" className="error">{message}</div>} />
                                    </div>
                                    {watchPaymentOption === "Split Bill" && (
                                        <div className="col-sm-6 col-md-6 form-item form-group">
                                            <label htmlFor="employerGroupPremiumAmount" className="control-label js-form-required form-required">
                                                Employer Group Premium Amount
                                            </label>
                                            <input
                                                id="employerGroupPremiumAmount"
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                className="form-text required form-control"
                                                {...register("employerGroupPremiumAmount", {
                                                    validate: (val) =>
                                                        watchPaymentOption !== "Split Bill" || !!val || "Employer Group Premium Amount is required.",
                                                })}
                                            />
                                            <ErrorMessage errors={errors} name="employerGroupPremiumAmount"
                                                render={({ message }) => <div role="alert" aria-live="polite" className="error">{message}</div>} />
                                        </div>
                                    )}

                                    <div className="col-sm-6 col-md-6 form-item form-group">
                                        <label className="control-label">
                                            <input type="checkbox" {...register("lateEnrollmentPayIndicator")} />
                                            {" "}Late Enrollment Member Pay Indicator
                                        </label>
                                    </div>

                                </div>

                                {/* Total Premium Amount */}
                                <div className="row form-group">
                                    <div className="col-sm-6 col-md-6 form-item form-group">
                                        <label htmlFor="totalPremiumAmount" className="control-label js-form-required form-required">
                                            Total Premium Amount
                                        </label>
                                        <input
                                            id="totalPremiumAmount"
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            className="form-text required form-control"
                                            {...register("totalPremiumAmount", { required: "Total Premium Amount is required." })}
                                        />
                                        <ErrorMessage errors={errors} name="totalPremiumAmount"
                                            render={({ message }) => <div role="alert" aria-live="polite" className="error">{message}</div>} />
                                    </div>
                                </div>
                            </div>

                            {/* ── Error dialog ─────────────────────────────────────────────── */}
                            <MessageDialog {...errorDialogProps} />

                            {/* ── Submit ───────────────────────────────────────────────────── */}
                            <Button
                                type="submit"
                                className="webform-button--next button js-form-submit form-submit btn-default btn"
                                disabled={isProcessing}
                                style={{ marginTop: "24px" }}
                            >
                                {isProcessing ? "Submitting…" : "Submit Renewal"}
                            </Button>

                        </form>
                    </div>
                </section>
            </div>
        </div>
    );
};

export default GroupRenewal;
